import { defineStore } from 'pinia'
import useAuthStore from './auth'
import useRestaurantStore from './restaurant'
import { fetchUserLoyaltyTags } from '~/composables/api'
import { STORE_ID } from '~/constants/store'
import type { Coupon } from '~/types/api/data-contracts'
import type { RelishState as State } from '~/types/relish'
import { isExpectedApiNetworkControlError } from '~/utils/apiError'
import { toMadBundayAflReadableString, toMadBundayNrlReadableString } from '~/utils/madBundayApiTranslation'

const initialState = (): State => ({
  coupons: [],
  eightAndDonate: {},
  productRecommendations: [],
  tags: undefined,
  madBundayEntries: {
    id: null,
    code: null,
    team: null,
    entriesCount: undefined,
    bonusEntriesCount: undefined
  },
  fetching: {
    coupons: false,
    eightAndDonate: false,
    productRecommendations: false,
    tags: false,
    madBundayEntries: false
  },
  fetchDidFail: {
    coupons: false,
    eightAndDonate: false,
    productRecommendations: false,
    tags: false,
    madBundayEntries: false
  },
  lastUpdated: {
    coupons: undefined,
    eightAndDonate: undefined,
    productRecommendations: undefined,
    tags: undefined,
    madBundayEntries: undefined
  },
  lastFailed: {
    coupons: undefined,
    eightAndDonate: undefined,
    productRecommendations: undefined,
    tags: undefined,
    madBundayEntries: undefined
  }
})

/**
 * @name useRelishStore
 * @description Store for Relish membership data
 *
 */
const useRelishStore = defineStore(STORE_ID.RELISH, {
  state: initialState,

  getters: {
    getCouponById: (state) => {
      return (hash: string): Coupon | null => {
        return state.coupons.find((coupon) => coupon.hash === hash) || null
      }
    },
    getCouponByProgramId: (state) => {
      return (programId: number): Coupon | null => {
        return state.coupons.find((coupon) => coupon.programId === programId) || null
      }
    },
    hasEightAndDonateHistory(): boolean {
      return !!this.eightAndDonate.cardCount || !!this.eightAndDonate.stampCount
    }
  },

  actions: {
    /**
     * @description Fetches the user's coupons
     */
    async fetchCoupons(): Promise<void> {
      const authStore = useAuthStore()

      if (!authStore.isAuthenticated) {
        throw new Error('User must be authenticated to get coupons')
      }

      this.fetching.coupons = true
      try {
        this.coupons = await fetchCoupons()
        this.lastUpdated.coupons = Date.now()
        this.lastFailed.coupons = undefined
        this.fetchDidFail.coupons = false
      } catch (error: any) {
        this.fetchDidFail.coupons = true
        this.lastFailed.coupons = Date.now()
        if (isExpectedApiNetworkControlError(error)) {
          return
        }
        throw new Error(`${error.name}: ${error.message}`)
      } finally {
        this.fetching.coupons = false
      }
    },
    /**
     * @description Fetches the user's Eight and Donate data
     */
    async fetchEightAndDonate(): Promise<void> {
      const authStore = useAuthStore()

      if (!authStore.authUser?.id) {
        throw new Error('User must be authenticated to get loyalty card')
      }

      this.fetching.eightAndDonate = true
      try {
        this.eightAndDonate = await getEightAndDonate(authStore.authUser.id)
        this.lastUpdated.eightAndDonate = Date.now()
        this.lastFailed.eightAndDonate = undefined
        this.fetchDidFail.eightAndDonate = false
      } catch (error) {
        this.fetchDidFail.eightAndDonate = true
        this.lastFailed.eightAndDonate = Date.now()
        if (isExpectedApiNetworkControlError(error)) {
          return
        }
        console.error(error)
        throw Error('Could not fetch loyalty data')
      } finally {
        this.fetching.eightAndDonate = false
      }
    },
    /**
     * @description Fetches the user's loyalty tags
     * @param emit {boolean} - Whether to emit the user tags to the marketing app
     * @returns {Promise<void>}
     */
    async fetchLoyaltyTags(emit = true): Promise<void> {
      const authStore = useAuthStore()

      if (!authStore.authUser?.id) {
        throw new Error('User must be authenticated to get tags')
      }

      try {
        this.tags = await fetchUserLoyaltyTags(authStore.authUser.id)
        this.lastUpdated.tags = Date.now()
        this.lastFailed.tags = undefined
        this.fetchDidFail.tags = false
      } catch (error) {
        this.lastFailed.tags = Date.now()
        this.fetchDidFail.tags = true
        console.warn(error)
      }

      if (emit) {
        emitUserTags()
      }
    },
    async fetchProductRecommendations(): Promise<void> {
      if (this.fetching.productRecommendations) {
        return
      }
      this.fetching.productRecommendations = true
      const authStore = useAuthStore()
      const restaurantStore = useRestaurantStore()

      if (!authStore.authUser?.id) {
        throw new Error('User must be authenticated to get recommendations')
      }

      if (!restaurantStore.restaurant?.id) {
        throw new Error('Cannot fetch product recommendations without a restaurant')
      }

      if (!restaurantStore.orderConfig.orderType) {
        throw new Error('Cannot fetch product recommendations without an order type')
      }

      try {
        // If in catering mode or if the restaurant is a demo restaurant, set product recommendations to an empty array
        if (restaurantStore.isDemoRestaurant || restaurantStore.isCateringMode) {
          this.productRecommendations = []
        } else {
          this.productRecommendations = await getProductRecommendations({
            userId: authStore.authUser.id,
            restaurantId: restaurantStore.restaurant.id,
            orderType: restaurantStore.orderConfig.orderType
          })
        }
        this.fetchDidFail.productRecommendations = false
        this.lastUpdated.productRecommendations = Date.now()
        this.lastFailed.productRecommendations = undefined
      } catch (error) {
        this.fetchDidFail.productRecommendations = true
        this.lastFailed.productRecommendations = Date.now()
        if (isExpectedApiNetworkControlError(error)) {
          return
        }
        console.error(error)
        throw Error('Could not fetch product recommendations')
      } finally {
        this.fetching.productRecommendations = false
      }
    },
    async fetchMadBundayEntries(): Promise<void> {
      const authStore = useAuthStore()

      if (!authStore.isAuthenticated) {
        throw new Error('User must be authenticated to get Mad Bunday entries')
      }

      this.fetching.madBundayEntries = true
      try {
        const entries = await getMadBundayEntries(authStore.authUser!.id!)
        entries.team =
          entries.code === 'AFL'
            ? toMadBundayAflReadableString(entries.team!)
            : toMadBundayNrlReadableString(entries.team!)
        this.madBundayEntries = entries
        this.lastUpdated.madBundayEntries = Date.now()
        this.lastFailed.madBundayEntries = undefined
      } catch (error) {
        this.fetchDidFail.madBundayEntries = true
        this.lastFailed.madBundayEntries = Date.now()
        // Ignore error fetching Mad Bunday entries as it will return 400 if the user is not in the Mad Bunday program
        console.warn('Could not fetch Mad Bunday entries')
      } finally {
        this.fetching.madBundayEntries = false
      }
    }
  }
})

export default useRelishStore
export { initialState }
